/*
 * Registration link & external-website settings — the organizer's side of
 * docs/registration-api.md.
 *
 * Opens from the "Online Registration" block of the tournament edit modal. Two tabs:
 *
 *   LINK (default) — the shareable https://.../r/<id> link, its QR code and an iframe
 *       snippet. This is what almost every organizer wants, and it needs no keys, no
 *       endpoints and no developer, so it is what they see first.
 *   API (opt-in)   — endpoint, tournament id, keys and the paste-in script, for a site
 *       that posts from its own form.
 *
 * Below the tabs is a shortcut into the field configuration, which both channels
 * share — and which the app's own Register button uses too, so it lives in its own
 * modal (js/ui/registration-fields.js) reachable from the tournament's registration
 * options as well as from here.
 *
 * This modal owns `registrationApi.enabled` and `registrationApi.linkEnabled` — the
 * two off switches for the outside world — and nothing else on that object. It is
 * committed by this modal's own Save button, independently of the edit modal
 * underneath it. Keys are created and revoked immediately (they are their own
 * documents).
 */

import { state, saveToLocalStorage } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import {
    openRegistrationFieldsModal,
    renderRegistrationFieldsSummaries,
    readRegistrationConfig,
    currentTournament,
    isDraftMode
} from './registration-fields.js';

// Base URL of the API. The Hosting rewrite (firebase.json) keeps this on the app's
// own domain, so the endpoint survives the function ever moving or being renamed.
const API_BASE = `${location.origin}/api`;
const DOCS_URL = `${location.origin}/docs/registration-api.md`;

// The keyless shareable link. Rendered by the Cloud Function so each one carries
// its own Open Graph tags (see handleRegistrationPage in functions/index.js).
const linkFor = (tournamentId) => `${location.origin}/r/${tournamentId}`;

// Working copies; committed to the tournament on Save.
let draftEnabled = true;
let draftLinkEnabled = true;
// Which tab is showing. Always reset to 'link' on open — the API tab is somewhere you
// go on purpose, never somewhere you land.
let activeTab = 'link';
let keys = [];
// Have the keys been fetched for this modal session? (Fetched lazily, on the first
// visit to the API tab.)
let keysLoaded = false;
// Plaintext of a key that was just created — shown once, never persisted.
let freshKey = null;

const $ = (id) => document.getElementById(id);

function modal() { return $('registration-api-modal'); }

// currentTournament() and isDraftMode() are shared with the fields modal (imported
// above): the two panels must agree on WHICH tournament they are configuring, and
// while the create form is open that is not state.activeTournament — see the
// comment on currentTournament() in registration-fields.js.

function isOwner() {
    const t = currentTournament();
    const user = window.authApp && window.authApp.getCurrentUser && window.authApp.getCurrentUser();
    return !!(t && user && (!t.ownerId || t.ownerId === user.uid));
}

// ---------------------------------------------------------------- open/close

export function openRegistrationApiModal() {
    // In create mode there is no tournament object to read from yet, so the panel
    // reads back whatever was staged earlier in this same create session.
    const cfg = readRegistrationConfig();
    draftEnabled = cfg.enabled !== false;
    // Defaults to on: a public tournament already shows a Register button to every
    // spectator, so the link is a shortcut to something already offered.
    draftLinkEnabled = cfg.linkEnabled !== false;
    freshKey = null;
    keys = [];
    keysLoaded = false;
    activeTab = 'link';

    renderAll();
    modal().classList.add('active');
    // Keys are NOT fetched here: they live in their own collection and only the API
    // tab shows them, so the query waits until someone actually opens that tab.
}

export function closeRegistrationApiModal() {
    modal().classList.remove('active');
    freshKey = null;
}

// ------------------------------------------------------------------ renders

function renderAll() {
    const t = currentTournament();
    const draft = isDraftMode();

    $('reg-api-enabled').checked = draftEnabled;
    // In create mode the name field of the form is the only name that exists yet.
    const nameInput = document.getElementById('tourney-name');
    $('reg-api-tournament-name').textContent =
        (t && t.name) || (nameInput && nameInput.value.trim()) || tr('this tournament');
    $('reg-api-endpoint').value = `${API_BASE}/v1/registrations`;
    $('reg-api-tournament-id').value = draft ? '' : t.id;
    $('reg-api-docs-link').href = DOCS_URL;

    renderTabs();
    renderRegistrationFieldsSummaries();
    renderLink();
    renderGroupHint();
    renderKeys();
    renderSnippet();

    // Everything that needs a tournament that actually exists in Firestore.
    $('reg-api-not-saved-link').style.display = draft ? 'block' : 'none';
    $('reg-api-link-block').style.display = (!draft && draftLinkEnabled) ? 'block' : 'none';
    $('reg-api-not-saved-api').style.display = draft ? 'block' : 'none';

    // Only the owner may mint credentials; a co-arbiter can still read the setup.
    const ownerOnly = $('reg-api-owner-only');
    if (ownerOnly) ownerOnly.style.display = (!draft && !isOwner()) ? 'block' : 'none';
    $('reg-api-key-create-row').style.display = (!draft && isOwner()) ? 'flex' : 'none';
    $('reg-api-keys').style.display = draft ? 'none' : 'flex';
}

function renderTabs() {
    ['link', 'api'].forEach(tab => {
        const btn = $('btn-reg-api-tab-' + tab);
        const panel = $('reg-api-tab-' + tab);
        if (btn) btn.classList.toggle('active', activeTab === tab);
        if (panel) panel.style.display = activeTab === tab ? 'block' : 'none';
    });
    // The API section's own contents are pointless while the API is switched off.
    const apiBlock = $('reg-api-block');
    if (apiBlock) apiBlock.style.display = draftEnabled ? 'block' : 'none';
}

function showTab(tab) {
    activeTab = tab === 'api' ? 'api' : 'link';
    renderTabs();
    // Keys are only fetched when the API tab is first opened, so an organizer who
    // never touches it never pays for the query. A tournament that does not exist
    // yet has none to fetch.
    if (activeTab === 'api' && !keysLoaded && !isDraftMode()) refreshKeys();
}

// The shareable link, its QR code and the iframe snippet that wraps it.
function renderLink() {
    const t = currentTournament();
    $('reg-api-link-enabled').checked = draftLinkEnabled;

    // No id yet means no link yet; renderAll() shows the "once you create it" note
    // in place of this block, so there is nothing to build.
    if (isDraftMode()) return;

    const link = linkFor(t.id);
    $('reg-api-link').value = link;
    $('reg-api-link-block').style.display = draftLinkEnabled ? 'block' : 'none';
    $('reg-api-iframe').value =
`<iframe src="${link}?embed=1" style="border:0;width:100%;max-width:620px;height:720px"
        loading="lazy" title="${(t.name || '').replace(/"/g, "'")}"></iframe>`;

    // The QR is for posters and invitations, so it is generated at print-friendly
    // size and regenerated whenever the modal reopens (the container is reused).
    const host = $('reg-api-link-qr');
    if (host && window.QRCode) {
        host.innerHTML = '';
        new window.QRCode(host, {
            text: link,
            width: 132,
            height: 132,
            correctLevel: window.QRCode.CorrectLevel.M
        });
    }

    // A link to a non-public tournament resolves to a "registration closed" page,
    // which is confusing to hand out; say so where the organizer is looking. Read
    // the live checkbox, not the saved flag — the edit modal underneath may have
    // it toggled but not yet saved.
    const publicToggle = document.getElementById('tourney-public');
    const isPublic = publicToggle ? publicToggle.checked : !!t.isPublic;
    $('reg-api-link-warning').style.display = isPublic ? 'none' : 'block';
}

// Tell the organizer which groupId external forms must send.
function renderGroupHint() {
    const t = currentTournament();
    const host = $('reg-api-groups');
    const groups = (t && Array.isArray(t.groups)) ? t.groups : [];
    if (groups.length === 0) {
        host.innerHTML = `<p style="font-size: 0.8rem; color: var(--text-muted); margin: 0;">${escapeHtml(tr('This tournament has one group, so the external form does not need to send a group.'))}</p>`;
        return;
    }
    host.innerHTML = `
        <p style="font-size: 0.8rem; color: var(--text-muted); margin: 0 0 0.5rem;">${escapeHtml(tr('This tournament has several groups, so every submission must include a groupId:'))}</p>
        <div style="display: flex; flex-direction: column; gap: 0.25rem;">
            ${groups.map(g => `
                <div style="display: flex; justify-content: space-between; gap: 0.75rem; font-size: 0.82rem;">
                    <span style="color: var(--text-main);">${escapeHtml(g.name || tr('Group'))}</span>
                    <span style="font-family: monospace; color: var(--text-muted);">${escapeHtml(String(g.id))}${g.registrationOpen ? '' : ' · ' + escapeHtml(tr('registration closed'))}</span>
                </div>`).join('')}
        </div>`;
}

function renderKeys() {
    const host = $('reg-api-keys');
    const t = currentTournament();

    if (freshKey) {
        $('reg-api-fresh').style.display = 'block';
        $('reg-api-fresh-value').value = freshKey;
    } else {
        $('reg-api-fresh').style.display = 'none';
    }

    if (keys === null) {
        host.innerHTML = `<p style="font-size: 0.8rem; color: var(--text-muted);">${escapeHtml(tr('Loading...'))}</p>`;
        return;
    }
    // Keys scoped to another tournament are irrelevant here.
    const relevant = keys.filter(k => !k.tournamentId || String(k.tournamentId) === String(t.id));
    if (relevant.length === 0) {
        host.innerHTML = `<p style="font-size: 0.8rem; color: var(--text-muted); margin: 0;">${escapeHtml(tr('No keys yet. Create one to let an external website register players.'))}</p>`;
        return;
    }

    host.innerHTML = relevant.map(k => {
        const scope = k.tournamentId ? tr('This tournament only') : tr('All my tournaments');
        const origins = (k.allowedOrigins || []).join(', ');
        const badge = k.type === 'secret'
            ? `<span class="status-badge" style="background: rgba(59,130,246,0.15); color: #60a5fa;">${escapeHtml(tr('Secret'))}</span>`
            : `<span class="status-badge" style="background: rgba(16,185,129,0.15); color: #34d399;">${escapeHtml(tr('Public'))}</span>`;
        const dead = k.active === false;
        return `
            <div style="border: 1px solid var(--border-color); border-radius: 8px; padding: 0.6rem 0.75rem; display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; ${dead ? 'opacity: 0.55;' : ''}">
                <div style="min-width: 0;">
                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                        ${badge}
                        <span style="font-weight: 600; color: var(--text-main); font-size: 0.9rem;">${escapeHtml(k.label || tr('Unnamed key'))}</span>
                        ${dead ? `<span class="status-badge muted">${escapeHtml(tr('Revoked'))}</span>` : ''}
                    </div>
                    <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 0.15rem;">
                        ${escapeHtml(scope)}${origins ? ' · ' + escapeHtml(origins) : ''}
                        ${k.type === 'public' && !origins ? ' · <span style="color: var(--warning);">' + escapeHtml(tr('no websites allowed yet')) + '</span>' : ''}
                    </div>
                    ${k.type === 'public' ? `<div style="font-size: 0.7rem; font-family: monospace; color: var(--text-muted); overflow-wrap: anywhere;">${escapeHtml(k.publicKey || '')}</div>` : ''}
                </div>
                <div style="display: flex; gap: 0.35rem; flex-shrink: 0;">
                    ${isOwner() && k.type === 'public' ? `<button type="button" class="btn-icon-small reg-api-key-origins" data-key-id="${escapeHtml(k.id)}" title="${escapeHtml(tr('Allowed websites'))}"><i class="ri-global-line"></i></button>` : ''}
                    ${isOwner() ? `<button type="button" class="btn-icon-small btn-icon-danger reg-api-key-delete" data-key-id="${escapeHtml(k.id)}" title="${escapeHtml(tr('Revoke and delete'))}"><i class="ri-delete-bin-line"></i></button>` : ''}
                </div>
            </div>`;
    }).join('');

    host.querySelectorAll('.reg-api-key-delete').forEach(btn => {
        btn.addEventListener('click', () => deleteKey(btn.dataset.keyId));
    });
    host.querySelectorAll('.reg-api-key-origins').forEach(btn => {
        btn.addEventListener('click', () => editOrigins(btn.dataset.keyId));
    });
}

// The copy-paste block for the external site. Uses a real public key when one
// exists, otherwise a clearly-marked placeholder.
function renderSnippet() {
    const t = currentTournament();
    const pub = (keys || []).find(k => k.type === 'public' && k.active !== false &&
        (!k.tournamentId || String(k.tournamentId) === String(t.id)));
    const keyValue = pub ? pub.publicKey : 'ts_pk_YOUR_PUBLIC_KEY';
    $('reg-api-snippet').value =
`<div data-zugzwang-registration
     data-api="${API_BASE}"
     data-key="${keyValue}"
     data-tournament="${t.id}"></div>
<script src="${location.origin}/embed/registration-form.js" defer></script>`;
}

// ------------------------------------------------------------------ actions

async function refreshKeys() {
    if (!window.authApp || !window.authApp.listApiKeys) return;
    keys = null;
    renderKeys();
    try {
        keys = await window.authApp.listApiKeys();
    } catch (err) {
        console.error('Failed to load API keys', err);
        keys = [];
    }
    keysLoaded = true;
    renderKeys();
    renderSnippet();
}

async function createKey() {
    const t = currentTournament();
    const type = $('reg-api-new-type').value === 'public' ? 'public' : 'secret';
    const label = $('reg-api-new-label').value.trim();
    const scopeAll = $('reg-api-new-scope').value === 'all';

    let allowedOrigins = [];
    if (type === 'public') {
        const raw = await window.Dialog.text(
            tr('Which website addresses may use this key? Separate several with commas.'),
            tr('Allowed Websites'),
            '',
            tr('Create Key'),
            'https://klubb.no, *.klubb.no'
        );
        if (raw === null) return;
        allowedOrigins = parseOrigins(raw);
        if (allowedOrigins.length === 0) {
            window.Dialog.warn(tr('A public key needs at least one website address, otherwise no browser may use it.'), tr('Missing Websites'));
            return;
        }
    }

    const btn = $('btn-reg-api-create-key');
    btn.disabled = true;
    try {
        const { key, record } = await window.authApp.createApiKey({
            type,
            label: label || (type === 'secret' ? tr('Server key') : tr('Website key')),
            tournamentId: scopeAll ? null : t.id,
            allowedOrigins
        });
        keys.unshift(record);
        // A secret key is stored hashed: this is the only moment it can be read.
        freshKey = type === 'secret' ? key : null;
        $('reg-api-new-label').value = '';
        renderKeys();
        renderSnippet();
        if (type === 'public') {
            window.Dialog.success(tr('Public key created. It is safe to paste into a web page — only the websites you listed can use it.'), tr('Key Created'));
        }
    } catch (err) {
        console.error(err);
        window.Dialog.error(tr('Could not create the key:') + ' ' + err.message, tr('Error'));
    } finally {
        btn.disabled = false;
    }
}

function parseOrigins(raw) {
    return String(raw || '')
        .split(',')
        .map(s => s.trim().toLowerCase().replace(/\/+$/, ''))
        .filter(Boolean)
        .slice(0, 20);
}

async function editOrigins(keyId) {
    const key = keys.find(k => k.id === keyId);
    if (!key) return;
    // Dialog.text resolves to null on cancel AND on an empty box, so an empty
    // answer cannot be told apart from "cancel" — clearing every address is done
    // by revoking the key instead.
    const raw = await window.Dialog.text(
        tr('Which website addresses may use this key? Separate several with commas.'),
        tr('Allowed Websites'),
        (key.allowedOrigins || []).join(', '),
        tr('Save'),
        'https://klubb.no, *.klubb.no'
    );
    if (raw === null) return;
    const allowedOrigins = parseOrigins(raw);
    try {
        await window.authApp.updateApiKey(keyId, { allowedOrigins });
        key.allowedOrigins = allowedOrigins;
        renderKeys();
    } catch (err) {
        window.Dialog.error(tr('Could not update the key:') + ' ' + err.message, tr('Error'));
    }
}

async function deleteKey(keyId) {
    const key = keys.find(k => k.id === keyId);
    const confirmed = await window.Dialog.confirm(
        tr('Revoke "{label}"? Any website still using it will stop being able to register players immediately.', { label: (key && key.label) || tr('this key') }),
        tr('Revoke Key'),
        tr('Revoke'),
        true,
        tr('Cancel')
    );
    if (!confirmed) return;
    try {
        await window.authApp.deleteApiKey(keyId);
        keys = keys.filter(k => k.id !== keyId);
        renderKeys();
        renderSnippet();
    } catch (err) {
        window.Dialog.error(tr('Could not revoke the key:') + ' ' + err.message, tr('Error'));
    }
}

function saveConfig() {
    // MERGE onto whatever is stored: the questions themselves belong to the fields
    // modal and the access code to the booking flow, and both live on this same
    // object. Writing a fresh one here would wipe them.
    const config = Object.assign({}, readRegistrationConfig(), {
        enabled: !!$('reg-api-enabled').checked,
        linkEnabled: !!$('reg-api-link-enabled').checked
    });

    if (isDraftMode()) {
        // The tournament does not exist yet: stage the configuration and let the
        // create form's submit handler attach it to the tournament it builds.
        state.draftRegistrationApi = config;
        closeRegistrationApiModal();
        window.Dialog.alert(
            tr('Saved with the tournament when you create it.'),
            tr('Ready')
        );
        return;
    }

    const t = currentTournament();
    if (!t) return;
    t.registrationApi = config;

    saveToLocalStorage();
    if (window.authApp && window.authApp.scheduleSave) window.authApp.scheduleSave(state);

    closeRegistrationApiModal();
    window.Dialog.success(tr('Registration settings saved.'), tr('Saved'));
}

async function copyFrom(inputId, btn) {
    const el = $(inputId);
    if (!el) return;
    try {
        await navigator.clipboard.writeText(el.value);
    } catch (e) {
        el.select();
        document.execCommand('copy');
    }
    if (btn) {
        const original = btn.innerHTML;
        btn.innerHTML = '<i class="ri-check-line"></i>';
        setTimeout(() => { btn.innerHTML = original; }, 1200);
    }
}

// -------------------------------------------------------------------- wiring

export function initRegistrationApiUI() {
    const openBtn = $('btn-registration-api');
    if (openBtn) openBtn.addEventListener('click', openRegistrationApiModal);

    const m = modal();
    if (!m) return;

    m.querySelectorAll('.btn-close-modal').forEach(btn => {
        btn.addEventListener('click', closeRegistrationApiModal);
    });
    m.addEventListener('click', (e) => { if (e.target === m) closeRegistrationApiModal(); });

    $('btn-reg-api-save').addEventListener('click', saveConfig);
    $('btn-reg-api-create-key').addEventListener('click', createKey);

    m.querySelectorAll('[data-reg-api-tab]').forEach(btn => {
        btn.addEventListener('click', () => showTab(btn.dataset.regApiTab));
    });

    $('reg-api-link-enabled').addEventListener('change', () => {
        draftLinkEnabled = $('reg-api-link-enabled').checked;
        renderLink();
    });
    $('reg-api-enabled').addEventListener('change', () => {
        draftEnabled = $('reg-api-enabled').checked;
        renderTabs();
    });
    $('btn-reg-api-open-link').addEventListener('click', () => {
        window.open($('reg-api-link').value, '_blank', 'noopener');
    });

    // The questions themselves are edited in their own modal, which stacks on top
    // of this one and writes straight through — so the summary is redrawn when it
    // closes rather than waiting for this modal's Save.
    $('btn-reg-api-edit-fields').addEventListener('click', () => {
        openRegistrationFieldsModal({ onSaved: renderRegistrationFieldsSummaries });
    });

    $('reg-api-new-type').addEventListener('change', () => {
        const isPublic = $('reg-api-new-type').value === 'public';
        $('reg-api-new-type-hint').textContent = isPublic
            ? tr('Goes in the web page itself. Locked to the website addresses you name.')
            : tr('For a server, plugin or script. Shown once — store it somewhere safe.');
    });

    m.querySelectorAll('[data-copy-target]').forEach(btn => {
        btn.addEventListener('click', () => copyFrom(btn.dataset.copyTarget, btn));
    });
}
